/**
 * REQ-104 phase B: the Radio layer (US college, low-power and non-commercial FM stations from FCC data,
 * baked by scripts/bake-radio-stations.cjs). Plain Mapbox calls, no React, so Renee's layer control can reuse it.
 *
 * syncRadioLayer is idempotent: call it whenever visibility, data or the map style changes (a style switch
 * wipes custom sources, so TourPlanner also calls it on 'styledata').
 */
export const RADIO_SOURCE_ID = 'radio-stations-source';
export const RADIO_POINT_LAYER = 'radio-stations-points';
export const RADIO_LABEL_LAYER = 'radio-stations-labels';

export const RADIO_TYPE_LABELS = {
  college: 'College radio',
  lpfm: 'Low-power community radio',
  noncommercial: 'Non-commercial radio',
};

const EMPTY = { type: 'FeatureCollection', features: [] };

// Only write when it changes: setLayoutProperty fires 'styledata', and TourPlanner re-syncs on 'styledata'.
function setVisibility(map, id, value) {
  if (!map.getLayer(id)) return;
  if ((map.getLayoutProperty(id, 'visibility') || 'visible') !== value) map.setLayoutProperty(id, 'visibility', value);
}

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

/** Popup body for one station feature's properties (s, f, c, st, t, n). */
export function buildRadioPopupHtml(props = {}) {
  const call = String(props.s || '').trim();
  const freq = Number(props.f);
  const freqText = Number.isFinite(freq) ? `${freq.toFixed(1)} FM` : '';
  const place = [props.c, props.st].filter(Boolean).join(', ');
  const type = RADIO_TYPE_LABELS[props.t] || 'Radio station';
  const query = encodeURIComponent([call, freqText, 'radio', place].filter(Boolean).join(' '));
  return `
    <div style="min-width:200px;max-width:260px;font-family:system-ui,-apple-system,sans-serif;padding:12px 12px 10px;">
      <p style="margin:0 0 4px 0;color:rgba(255,255,255,0.5);font-size:10.5px;letter-spacing:0.08em;text-transform:uppercase;font-weight:600;">${escapeHtml(type)}</p>
      <p style="margin:0;color:#fff;font-size:16px;font-weight:700;letter-spacing:0.02em;">${escapeHtml(call)}${freqText ? ` <span style="font-weight:500;color:rgba(255,255,255,0.75);">${escapeHtml(freqText)}</span>` : ''}</p>
      ${place ? `<p style="margin:2px 0 0 0;color:rgba(255,255,255,0.75);font-size:12.5px;">${escapeHtml(place)}</p>` : ''}
      ${props.n ? `<p style="margin:6px 0 0 0;color:rgba(255,255,255,0.55);font-size:11.5px;line-height:1.4;">${escapeHtml(props.n)}</p>` : ''}
      <a href="https://www.google.com/search?q=${query}" target="_blank" rel="noopener noreferrer"
        style="display:inline-block;margin-top:9px;color:#fff;font-size:12px;font-weight:600;text-decoration:underline;">Find the station</a>
    </div>`;
}

/**
 * Make the radio source and layers match `visible` / `data`. Returns true when it touched the map.
 * `onClickFeature(feature, lngLat)` is wired once per map instance. A click that also hits one of
 * `yieldToLayers` (venue pins, say) is left to that layer, so two popups never stack.
 */
export function syncRadioLayer(map, { visible, data, color, onClickFeature, yieldToLayers = [] } = {}) {
  if (!map || typeof map.getSource !== 'function') return false;
  try {
    return applyRadioLayer(map, { visible, data, color, onClickFeature, yieldToLayers });
  } catch (_) {
    return false; // style mid-switch; the next 'styledata' call retries
  }
}

function applyRadioLayer(map, { visible, data, color, onClickFeature, yieldToLayers }) {
  const source = map.getSource(RADIO_SOURCE_ID);
  if (!visible) {
    [RADIO_LABEL_LAYER, RADIO_POINT_LAYER].forEach((id) => setVisibility(map, id, 'none'));
    return Boolean(source);
  }
  if (!source) {
    map.addSource(RADIO_SOURCE_ID, { type: 'geojson', data: data || EMPTY });
  } else if (data && source.__vrcData !== data) {
    source.setData(data);
  }
  const live = map.getSource(RADIO_SOURCE_ID);
  if (live && data) live.__vrcData = data;

  if (!map.getLayer(RADIO_POINT_LAYER)) {
    map.addLayer({
      id: RADIO_POINT_LAYER,
      type: 'circle',
      source: RADIO_SOURCE_ID,
      paint: {
        'circle-color': color || '#c43d72',
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 3, 2.5, 6, 4, 9, 6.5, 12, 9],
        'circle-opacity': 0.9,
        'circle-stroke-width': ['interpolate', ['linear'], ['zoom'], 4, 0.5, 9, 1.5],
        'circle-stroke-color': 'rgba(255,255,255,0.9)',
      },
    });
  }
  if (!map.getLayer(RADIO_LABEL_LAYER)) {
    map.addLayer({
      id: RADIO_LABEL_LAYER,
      type: 'symbol',
      source: RADIO_SOURCE_ID,
      minzoom: 9,
      layout: {
        'text-field': ['get', 's'],
        'text-size': 11,
        'text-offset': [0, 1.1],
        'text-anchor': 'top',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
      },
      paint: {
        'text-color': color || '#c43d72',
        'text-halo-color': 'rgba(255,255,255,0.9)',
        'text-halo-width': 1.2,
      },
    });
  }
  [RADIO_POINT_LAYER, RADIO_LABEL_LAYER].forEach((id) => setVisibility(map, id, 'visible'));

  if (!map.__vrcRadioHandlers && typeof map.on === 'function') {
    map.__vrcRadioHandlers = true;
    map.on('click', RADIO_POINT_LAYER, (e) => {
      const feature = e.features?.[0];
      if (!feature || !onClickFeature) return;
      const others = yieldToLayers.filter((id) => map.getLayer(id));
      if (others.length && map.queryRenderedFeatures(e.point, { layers: others }).length) return;
      onClickFeature(feature, e.lngLat);
    });
    map.on('mouseenter', RADIO_POINT_LAYER, () => { map.getCanvas().style.cursor = 'pointer'; });
    map.on('mouseleave', RADIO_POINT_LAYER, () => { map.getCanvas().style.cursor = ''; });
  }
  return true;
}

/** Fetch the baked file once per page load; later calls share the same promise. */
let radioDataPromise = null;
export function loadRadioStations(url, fetchImpl = typeof fetch === 'function' ? fetch : null) {
  if (!url || !fetchImpl) return Promise.reject(new Error('Radio layer has no data URL'));
  if (!radioDataPromise) {
    radioDataPromise = fetchImpl(url)
      .then((res) => {
        if (!res.ok) throw new Error(`Radio stations: HTTP ${res.status}`);
        return res.json();
      })
      .then((json) => {
        if (!json || !Array.isArray(json.features)) throw new Error('Radio stations: bad file');
        return json;
      })
      .catch((err) => {
        radioDataPromise = null; // allow a retry on the next toggle
        throw err;
      });
  }
  return radioDataPromise;
}
